import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { DexieRepository } from '../data/dexieRepository';
import type { Repository } from '../data/repository';
import { RepositoryProvider } from './repositoryContext';
import { ScheduleView } from './ScheduleView';
import { ClassesView } from './ClassesView';
import { MoreView } from './MoreView';
import { ClassesIcon, MoreIcon, ScheduleIcon } from './icons';

const TABS = [
  { id: 'schedule', label: 'Schedule', Icon: ScheduleIcon },
  { id: 'classes', label: 'Classes', Icon: ClassesIcon },
  { id: 'more', label: 'More', Icon: MoreIcon },
] as const;

export type TabId = (typeof TABS)[number]['id'];

export function App(props: {
  repository?: Repository;
  initialTab?: TabId;
  /** True when launched from the "Add a class" app shortcut. */
  startNewClass?: boolean;
}): JSX.Element {
  // One repository instance for the lifetime of the app; tests inject their own.
  const repository = useMemo(() => props.repository ?? new DexieRepository(), [props.repository]);
  const [tab, setTab] = useState<TabId>(
    props.initialTab ?? (props.startNewClass ? 'classes' : 'schedule'),
  );

  // Set when the schedule hands off to the class form, so Classes knows whether
  // to open straight into a form and for which series.
  const [editSeriesId, setEditSeriesId] = useState<string | null>(null);
  const [startNew, setStartNew] = useState(props.startNewClass ?? false);

  function openNewClass(): void {
    setEditSeriesId(null);
    setStartNew(true);
    setTab('classes');
  }

  function openSeries(seriesId: string): void {
    setStartNew(false);
    setEditSeriesId(seriesId);
    setTab('classes');
  }

  function clearFormIntent(): void {
    setStartNew(false);
    setEditSeriesId(null);
  }

  return (
    <RepositoryProvider value={repository}>
      <div class="app">
        <main class="app-body">
          {tab === 'schedule' ? (
            <ScheduleView onAddClass={openNewClass} onEditSeries={openSeries} />
          ) : null}
          {tab === 'classes' ? (
            <ClassesView
              editSeriesId={editSeriesId}
              startNew={startNew}
              onFormClosed={clearFormIntent}
            />
          ) : null}
          {tab === 'more' ? <MoreView /> : null}
        </main>

        <nav class="tabbar" aria-label="Sections">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              class={`tab${tab === entry.id ? ' selected' : ''}`}
              aria-current={tab === entry.id ? 'page' : undefined}
              onClick={() => {
                clearFormIntent();
                setTab(entry.id);
              }}
            >
              <entry.Icon />
              <span>{entry.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </RepositoryProvider>
  );
}
